import { BeatMappedChordProgression } from '@/javascript/chord-progression-identification';
import { Box, Button, Flex, Select, Text } from '@mantine/core';
import { IconWand } from '@tabler/icons-react';
import React from 'react';

interface ChordReductionsProps {
	runPitchQuantization: (measure: number, chord: BeatMappedChordProgression) => Promise<void>;
	onChordProgressionSelectionChange?: (measureNumber: number, chord: BeatMappedChordProgression) => void;
	chordProgressionsByMeasure: Map<number, { selection: BeatMappedChordProgression | undefined, options: BeatMappedChordProgression[] }>;
	showBeatLines: boolean;
	measureWidth: number;
	measureMarkerHeight: number;
	trackLabelWidth: number;
	heightOfTracks: number;
	numberOfMeasures: number;
	isTabletOrSmaller: boolean;
	isPhoneOrSmaller: boolean;
}

const ChordReductions: React.FC<ChordReductionsProps> =
	({
		runPitchQuantization,
		onChordProgressionSelectionChange,
		chordProgressionsByMeasure,
		showBeatLines,
		measureWidth,
		measureMarkerHeight,
		trackLabelWidth,
		numberOfMeasures,
		isTabletOrSmaller,
		isPhoneOrSmaller
	}) => {
		const [disableAll, setDisableAll] = React.useState<boolean>(false);
		const [disabledMeasureButtons, setDisabledMeasureButtons] = React.useState<Set<number>>(new Set());
		const chordReductionMarkers = Array.from({ length: numberOfMeasures }, (_, index) => {
			const chordCondidatesAtMeasure = chordProgressionsByMeasure.get(index);
			const options = chordCondidatesAtMeasure?.options ?? [];
			const selection = chordCondidatesAtMeasure?.selection || options[0];
			return (
				<Box
					key={`chord-name-${index}`}
					style={{
						height: measureMarkerHeight,
						position: "absolute",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "stretch",
						left: index * measureWidth,
						width: measureWidth
					}}
				>
					<Flex align="center" justify="space-between" gap="xs" w="100%" h="100%">
						{options?.length > 1 ? (
							<Select
								size="sm"
								data={options.map((chordProgression) => ({
									value: chordProgression.getDisplayName(),
									label: chordProgression.getDisplayName(),
								}))}
								value={selection?.getDisplayName() ?? ""}
								onChange={(value) => {
									if (onChordProgressionSelectionChange && value) {
										const selectedChord = options.find(chord => chord.getDisplayName() === value);
										if (selectedChord) {
											onChordProgressionSelectionChange(index, selectedChord);
										}
									}
								}}
								placeholder=""
								style={{ width: "90%" }}
								disabled={disableAll || disabledMeasureButtons.has(index)}
							/>
						) : (
							<Text
								fz={isPhoneOrSmaller ? 9 : isTabletOrSmaller ? 11 : "md"}
								style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
							>
								{options.length > 0 ? options[0].getDisplayName() : ""}
							</Text>
						)}

						<Button
							m={0}
							size="xs"
							variant="light"
							disabled={disableAll || disabledMeasureButtons.has(index)}
							onClick={async () => {
								if (selection) {
									setDisabledMeasureButtons(new Set(disabledMeasureButtons).add(index));
									await runPitchQuantization(index + 1, selection); // Add one to measure number because measures in the music are 1-indexed
									setDisabledMeasureButtons((prev) => {
										const newSet = new Set(prev);
										newSet.delete(index);
										return newSet;
									});
								}
							}}
						>
							<IconWand size={16} />
						</Button>
					</Flex>
					<span className={`measure-line${index % 4 === 0 ? ' bold' : ''}`} />
					{showBeatLines && (
						<>
							<span className="beat-line beat-line-1" />
							<span className="beat-line beat-line-2" />
							<span className="beat-line beat-line-3" />
						</>
					)}
				</Box>
			)
		});

		return (
			<div
				className={`timeline-track `}
				style={{
					height: measureMarkerHeight,
				}}
			>
				<div
					style={{
						width: trackLabelWidth,
						height: measureMarkerHeight,
					}}
					className={'track-label'}
				>
					<Button
						rightSection={<IconWand size={16} />}
						m={0}
						size="xs"
						variant="light"
						disabled={disableAll}
						onClick={async () => {
							setDisableAll(true);
							// iterate through all measures and quantize
							for (let index = 0; index < numberOfMeasures; index++) {
								const chordCondidatesAtMeasure = chordProgressionsByMeasure.get(index);
								const selection = chordCondidatesAtMeasure?.selection || chordCondidatesAtMeasure?.options[0];
								if (selection) {
									await runPitchQuantization(index + 1, selection); // Add one to measure number because measures in the music are 1-indexed
								}
							}
							setDisableAll(false);
							setDisabledMeasureButtons(new Set());
						}}
					>
						<Text
							fz={isTabletOrSmaller ? 10 : "xs"}
						>
							Quantize All
						</Text>
					</Button>
				</div>
				<div
					className="timeline-track-items-container"
					style={{
						height: measureMarkerHeight,
						left: trackLabelWidth
					}}
				>
					{chordReductionMarkers}
				</div>
			</div>
		);
	}

export default ChordReductions;
